/**
 * Patient Assessment Form PDF — admin-side regeneration.
 *
 * Some patients never got a PDF at intake (abandoned before the final step, upload
 * failure, etc.) even though their answers made it into `submissions.form_data`.
 * This service rebuilds that PDF on demand from the stored data using the exact
 * generator the intake client runs (`./pdfGenerator`), so the output matches what
 * the patient's own submission would have produced.
 *
 * Two things stand between the stored row and the generator:
 *
 *  1. The generator needs the tenant's form schema (modules + full field definitions),
 *     which lives behind the same public endpoints the intake client uses.
 *  2. The intake client post-processes its form values before POSTing them, so what
 *     is stored is not what the generator expects. `restore*` below undoes those
 *     transforms. Rows saved step-by-step (status = 'pending') are stored raw and
 *     pass through the restores untouched.
 */

import { environment } from '../env/environment';
import logger from '../utils/logger';

// ./pdfGenerator pulls in jspdf (~135 kB gzipped). Nothing on a normal admin page view
// needs it, so it is imported dynamically when a PDF is actually requested.
const loadGenerator = () => import('./pdfGenerator');

/**
 * Field-name normalisation. Must stay identical to the `toLowercase` helper inside
 * pdfGenerator.js — it is how the generator derives a field's key in the form data,
 * so any key we synthesise has to be built the same way or it will not be picked up.
 */
const toFieldKey = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .trim()
    .split(/\s+/)
    .join('')
    .toLowerCase();
};

// pdfGenerator resolves a field's key from labelName first — mirror that order.
const fieldKeyOf = (field) => toFieldKey(field?.labelName || field?.field_name || field?.name || '');

const fieldTypeOf = (field) => field?.type || field?.fieldType || field?.field_type || '';

// Matches the slug the intake client builds when it aggregates an accordion section.
const sectionSlug = (title) =>
  (title || '').toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/^_|_$/g, '');

// ---------------------------------------------------------------------------
// Form schema (modules + fields) for a tenant
// ---------------------------------------------------------------------------

// tenantId -> Promise<{ modules, fieldsMap }>. Schemas rarely change within a session
// and a single PDF needs one request per module, so cache the whole fetch.
const schemaCache = new Map();

const fetchModuleFields = async (moduleName, headers) => {
  const res = await fetch(
    `${environment.baseUrl}/api/modules/getFields/${encodeURIComponent(moduleName)}`,
    { headers }
  );
  if (!res.ok) {
    throw new Error(`Failed to load fields for "${moduleName}" (${res.status})`);
  }
  const data = await res.json();

  // Same shaping the intake client applies before handing fields to the generator.
  return (data.fields || []).map((field) => {
    if (field.type === 'radio' && field.optionsWithConditional) {
      return {
        ...field,
        optionsWithConditional: field.optionsWithConditional.map((cond, index) => ({
          ...cond,
          optionValue: field.options?.[index],
        })),
      };
    }
    return field;
  });
};

const loadFormSchema = async (tenantId) => {
  const headers = {};
  if (tenantId) headers['X-Tenant-ID'] = String(tenantId);

  const res = await fetch(`${environment.baseUrl}/api/modules/getAllModules`, { headers });
  if (!res.ok) throw new Error(`Failed to load form modules (${res.status})`);

  const data = await res.json();
  if (!Array.isArray(data.modules)) throw new Error('Invalid modules response from server');

  // The intake client sorts with this same comparator before generating; `count` is not
  // a per-module column, so it is a no-op that preserves the API's module_order. Kept
  // identical so section ordering can never drift from the patient-facing PDF.
  const modules = [...data.modules].sort((a, b) => a.count - b.count);

  const fieldsMap = {};
  await Promise.all(
    modules.map(async (mod) => {
      fieldsMap[mod.module_name] = await fetchModuleFields(mod.module_name, headers);
    })
  );

  return { modules, fieldsMap };
};

export const fetchFormSchema = (tenantId) => {
  const cacheKey = String(tenantId ?? 'default');
  if (!schemaCache.has(cacheKey)) {
    const pending = loadFormSchema(tenantId).catch((err) => {
      schemaCache.delete(cacheKey); // don't cache a failure
      throw err;
    });
    schemaCache.set(cacheKey, pending);
  }
  return schemaCache.get(cacheKey);
};

// ---------------------------------------------------------------------------
// Undo the intake client's pre-submit transforms
// ---------------------------------------------------------------------------

/**
 * Accordions are stored aggregated as
 *   accordion_<section_slug>: { question, selectedValues: [label], additionalInfo: {...} }
 * but the generator reads the per-checkbox keys
 *   <field>_section_<i>_checkbox_<j>[_input_<k>]
 * Rebuild those from the field definitions.
 */
const restoreAccordionCheckboxes = (formData, fieldsMap) => {
  const restored = { ...formData };

  Object.values(fieldsMap).forEach((fields) => {
    (fields || []).forEach((field) => {
      if (!Array.isArray(field?.accordionSections)) return;
      const base = fieldKeyOf(field);
      if (!base) return;

      field.accordionSections.forEach((section, sectionIndex) => {
        const slug = sectionSlug(section?.title);
        const stored = slug ? formData[`accordion_${slug}`] : null;
        if (!stored || typeof stored !== 'object') return;

        const selected = Array.isArray(stored.selectedValues) ? stored.selectedValues : [];
        const additionalInfo = stored.additionalInfo || {};

        (section.checkboxes || []).forEach((checkbox, checkboxIndex) => {
          if (!selected.includes(checkbox?.label)) return;

          const checkboxKey = `${base}_section_${sectionIndex}_checkbox_${checkboxIndex}`;
          restored[checkboxKey] = true;

          (checkbox.nestedInputs || []).forEach((nested, inputIndex) => {
            const value = additionalInfo[`${checkbox.label} - ${nested?.label}`];
            if (value !== undefined && value !== null && String(value).trim() !== '') {
              restored[`${checkboxKey}_input_${inputIndex}`] = value;
            }
          });
        });
      });
    });
  });

  return restored;
};

const CONTACT_PROP_BY_SUFFIX = {
  firstname: 'firstName',
  lastname: 'lastName',
  email: 'email',
  phone: 'phone',
  relationship: 'relationship',
  isemergency: 'isEmergencyContact',
  ismedicalauth: 'isAuthorizedMedical',
};

const CONTACT_KEY_PATTERN = new RegExp(
  `^authorizedcontact(\\d+)(${Object.keys(CONTACT_PROP_BY_SUFFIX).join('|')})$`
);

/**
 * Contacts are stored flattened as authorizedcontact1firstname, …ismedicalauth, etc.
 * The generator wants the original array on the authorized_contacts field.
 */
const restoreAuthorizedContacts = (formData, fieldsMap) => {
  const contactFields = [];
  Object.values(fieldsMap).forEach((fields) => {
    (fields || []).forEach((field) => {
      if (fieldTypeOf(field) === 'authorized_contacts') contactFields.push(field);
    });
  });
  if (contactFields.length === 0) return formData;

  const byIndex = new Map();
  Object.entries(formData).forEach(([key, value]) => {
    const match = CONTACT_KEY_PATTERN.exec(key.toLowerCase());
    if (!match) return;

    const index = parseInt(match[1], 10);
    const prop = CONTACT_PROP_BY_SUFFIX[match[2]];
    const contact = byIndex.get(index) || {};

    contact[prop] =
      prop === 'isEmergencyContact' || prop === 'isAuthorizedMedical'
        ? value === true || String(value).toLowerCase() === 'yes'
        : value;

    byIndex.set(index, contact);
  });
  if (byIndex.size === 0) return formData;

  const contacts = [...byIndex.keys()].sort((a, b) => a - b).map((i) => byIndex.get(i));
  const restored = { ...formData };

  contactFields.forEach((field) => {
    const key = fieldKeyOf(field);
    // A raw array already present (pending rows) is the better source — leave it alone.
    if (!key || (Array.isArray(restored[key]) && restored[key].length > 0)) return;
    restored[key] = contacts;
  });

  return restored;
};

// Uploads are stored as {name, type, size, _uploadPending} stubs — the file itself went
// to Drive, so the PDF can only name it rather than embed it.
const isUploadStub = (value) =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  typeof value.name === 'string' &&
  (value._uploadPending === true || (typeof value.size === 'number' && typeof value.type === 'string'));

const describeUploadStubs = (formData) => {
  const described = { ...formData };
  Object.entries(formData).forEach(([key, value]) => {
    if (isUploadStub(value)) {
      described[key] = value.name;
    } else if (Array.isArray(value) && value.length > 0 && value.every(isUploadStub)) {
      described[key] = value.map((file) => file.name).join(', ');
    }
  });
  return described;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const parseFormData = (raw) => {
  if (!raw) return {};
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const buildFileName = (submission) => {
  const name = [submission.patient_first_name, submission.patient_last_name]
    .filter(Boolean)
    .join('_')
    .replace(/[^A-Za-z0-9_-]/g, '') || 'patient';
  const date = new Date().toISOString().slice(0, 10);
  return `${name}_Patient_Assessment_Form_${date}.pdf`;
};

/**
 * Build the assessment-form PDF for a submission row as returned by
 * GET /api/admin/submissions.
 *
 * @param {object}  submission     Submission row (needs form_data, tenant_id, patient name).
 * @param {?string} tenantLogoUrl  Tenant logo; falls back to the bundled default logo.
 * @returns {Promise<Blob>}
 */
export const buildPatientPdfBlob = async ({ submission, tenantLogoUrl }) => {
  const formData = parseFormData(submission?.form_data);
  if (!formData || Object.keys(formData).length === 0) {
    throw new Error('This patient has no saved form data to build a PDF from.');
  }

  const [{ generatePDF }, { modules, fieldsMap }] = await Promise.all([
    loadGenerator(),
    fetchFormSchema(submission.tenant_id),
  ]);
  if (modules.length === 0) {
    throw new Error('No form modules are configured for this tenant.');
  }

  let values = restoreAccordionCheckboxes(formData, fieldsMap);
  values = restoreAuthorizedContacts(values, fieldsMap);
  values = describeUploadStubs(values);

  logger.info('[PatientPdf] Generating PDF', {
    submissionId: submission.submission_id,
    tenantId: submission.tenant_id,
    modules: modules.length,
  });

  // First argument is the generator's unused `painLevelsData` parameter; pain levels
  // reach the PDF as ordinary form fields (currentpainlevel/bestpainlevel/worstpainlevel).
  return generatePDF(null, values, modules, fieldsMap, false, tenantLogoUrl || null);
};

/**
 * Build the PDF and hand it to the browser as a download.
 */
export const downloadPatientPdf = async ({ submission, tenantLogoUrl }) => {
  const blob = await buildPatientPdfBlob({ submission, tenantLogoUrl });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = buildFileName(submission);
  document.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
